/*
 * Copyright (c) 2020 Staffino, s.r.o. - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Created by Martin Komara, April 2020
 */

import * as React from 'react';
import { useTheme } from '@emotion/react';
import { darken, desaturate, lighten, readableColor } from 'polished';
import {
  color as colorStyle,
  space,
  border,
  typography,
  layout,
  flexbox,
  ColorProps,
  SpaceProps,
  TypographyProps,
  LayoutProps,
  BorderProps,
  FlexboxProps,
} from 'styled-system';
import { TouchableOpacity } from '@staffino/primitives';
import styled from './styled';
import { Theme } from './theme';
import convertCssUnit from './convert-css-unit.native';

const variants = {
  primary: {
    color: 'primaryText',
    bg: 'primaryBackground',
  },
  secondary: {
    color: 'secondaryText',
    bg: 'secondaryBackground',
    disabledColor: '#ffffff',
    disabledBg: '#808080',
  },
  action: {
    color: 'actionText',
    bg: 'actionBackground',
  },
  light: {
    color: '#000',
    bg: '#fff',
  },
  link: {
    color: 'action',
    bg: 'transparent',
  },
};

export type ButtonProps<T> = T & {
  variant?: string;
  disabled?: boolean;
  bordered?: boolean;
  transparent?: boolean;

  bg?: string;
  disabledBg?: string;
  activeBg?: string;
  hoverBg?: string;
  color?: string;
  disabledColor?: string;
  activeColor?: string;
  hoverColor?: string;
  borderColor?: string;
  disabledBorderColor?: string;
  activeBorderColor?: string;
  hoverBorderColor?: string;

  block?: boolean;
  rounded?: boolean;
  borderRadius?: string;
  width?: number | string;

  className?: string;
  theme?: Theme;
  children?: React.ReactNode;
  textDecoration?: 'underline' | 'none';
  overflowWrap?: 'anywhere';

  onPress?: (event: React.SyntheticEvent) => void;
};

function smartDarken(color: string) {
  if (color === 'transparent') {
    return color;
  }
  return readableColor(color) === '#000'
    ? darken(0.1, color)
    : lighten(0.1, color);
}

function themeColor(color: string, theme: Theme): string {
  return theme?.colors?.[color] ?? color;
}

function calculateBackgroundColor<T>(
  props: ButtonProps<T>,
  theme: Theme,
): string {
  const { bg, disabledBg, variant, disabled, bordered, transparent } = props;

  if ((bordered || transparent) && !bg) {
    return 'transparent';
  }

  if (disabled) {
    // 1. specified color
    if (disabledBg) {
      return themeColor(disabledBg, theme);
    }
    // 2. tint color
    if (variant && variants[variant]?.disabledBg) {
      return themeColor(variants[variant].disabledBg, theme);
    }
    // 3. calculate from normal bg color
    const normalColor = calculateBackgroundColor(
      {
        ...props,
        disabled: false,
        bordered: false,
        transparent: false,
      },
      theme,
    );
    return desaturate(1, themeColor(normalColor, theme));
  }
  if (bg) {
    return themeColor(bg, theme);
  }
  const tintDefinition = (variant && variants[variant]) || variants.primary;
  return themeColor(tintDefinition.bg, theme);
}

function calculateActiveBackgroundColor<T>(
  props: ButtonProps<T>,
  theme: Theme,
): string {
  const { disabled, bordered, transparent, activeBg } = props;
  if (disabled || bordered || transparent) {
    return calculateBackgroundColor(props, theme);
  }
  if (activeBg) {
    return themeColor(activeBg, theme);
  }
  return smartDarken(calculateBackgroundColor(props, theme));
}

function calculateBorderColor<T>(props: ButtonProps<T>, theme: Theme): string {
  const { borderColor, disabledBorderColor, variant, disabled, transparent } =
    props;
  if (transparent) {
    return 'transparent';
  }
  if (disabled) {
    if (disabledBorderColor) {
      return themeColor(disabledBorderColor, theme);
    }
    if (variant && variants[variant]?.disabledBorderColor) {
      return themeColor(variants[variant]?.disabledBorderColor, theme);
    }
    return calculateBackgroundColor({ ...props, bordered: false }, theme);
  }
  if (borderColor) {
    return themeColor(borderColor, theme);
  }
  if (variant && variants[variant]?.borderColor) {
    return themeColor(variants[variant]?.borderColor, theme);
  }
  return calculateBackgroundColor({ ...props, bordered: false }, theme);
}

function calculateForegroundColor<T>(
  props: ButtonProps<T>,
  theme: Theme,
): string {
  const { color, disabledColor, variant, disabled, bordered, transparent } =
    props;
  if (bordered && !transparent) {
    // 1. use specified color
    if (color) {
      return themeColor(color, theme);
    }
    // 2. tint color
    if (variant && variants[variant]?.borderedColor) {
      return themeColor(variants[variant]?.borderedColor, theme);
    }
    // 3. use border color
    return calculateBorderColor(props, theme);
  }
  if (disabled) {
    if (disabledColor) {
      return themeColor(disabledColor, theme);
    }
    if (variant && variants[variant]?.disabledColor) {
      return themeColor(variants[variant]?.disabledColor, theme);
    }
    if (color) {
      return themeColor(color, theme);
    }
    // return light / dark
    const disabledBackground = calculateBackgroundColor(
      {
        ...props,
        disabled: true,
        bordered: false,
        transparent: false,
      },
      theme,
    );
    return readableColor(disabledBackground) === '#000'
      ? themeColor('headline', theme)
      : themeColor('primaryText', theme);
  }
  if (color) {
    return themeColor(color, theme);
  }
  const tintDefinition = (variant && variants[variant]) || variants.primary;
  return themeColor(tintDefinition.color, theme);
}

function calculateActiveForegroundColor<T>(
  props: ButtonProps<T>,
  theme: Theme,
): string {
  const { disabled, bordered, transparent, activeColor } = props;
  if (disabled) {
    return calculateForegroundColor(props, theme);
  }
  if (activeColor) {
    return themeColor(activeColor, theme);
  }
  if (bordered || transparent) {
    return smartDarken(calculateForegroundColor(props, theme));
  }
  return calculateForegroundColor(props, theme);
}

function calculateActiveBorderColor<T>(
  props: ButtonProps<T>,
  theme: Theme,
): string {
  const { disabled, transparent, activeBorderColor } = props;
  if (disabled || transparent) {
    return calculateBorderColor(props, theme);
  }
  if (activeBorderColor) {
    return themeColor(activeBorderColor, theme);
  }
  return smartDarken(calculateBorderColor(props, theme));
}

type StyledProps = ColorProps &
  SpaceProps &
  TypographyProps &
  LayoutProps &
  BorderProps &
  FlexboxProps;

export type StyledButtonProps = ButtonProps<
  React.ComponentProps<typeof TouchableOpacity>
> &
  StyledProps;

const StyledButton = styled(TouchableOpacity)(
  (props) => ({
    display: props.block ? 'flex' : 'inline-flex',
    cursor: props.disabled ? 'default' : 'pointer',
    textDecoration: props.textDecoration,
    overflowWrap: props.overflowWrap,
    '&:hover': {
      color: props.hoverColor,
      background: props.hoverBg,
      borderColor: props.hoverBorderColor,
    },
    '&:active': {
      color: props.activeColor,
      background: props.activeBg,
      borderColor: props.activeBorderColor,
    },
  }),
  colorStyle,
  space,
  typography,
  layout,
  border,
  flexbox,
);

export function buttonFactory<
  T extends object = React.ComponentProps<typeof StyledButton>,
>(Component = StyledButton) {
  function Button({
    children,
    variant = 'primary',
    bordered = false,
    borderWidth = convertCssUnit('1px'),
    px = convertCssUnit('3rem'),
    fontSize = convertCssUnit('0.875rem'),
    fontWeight = 'bold',
    textAlign = 'center' as const,
    flexDirection = 'row' as const,
    alignItems = 'center' as const,
    justifyContent = 'center',
    height = convertCssUnit('2.75rem'),
    ...props
  }: ButtonProps<T> & StyledProps) {
    const theme = useTheme() as Theme;
    const propsWithDefaults: ButtonProps<any> = {
      ...props,
      variant,
      bordered,
      borderWidth,
      px,
      fontSize,
      fontWeight,
      textAlign,
      flexDirection,
      alignItems,
      justifyContent,
      height,
      bg: calculateBackgroundColor(props, theme),
      hoverBg: calculateActiveBackgroundColor(props, theme),
      activeBg: calculateActiveBackgroundColor(props, theme),
      color: calculateForegroundColor(props, theme),
      hoverColor: calculateActiveForegroundColor(props, theme),
      activeColor: calculateActiveForegroundColor(props, theme),
      borderColor: calculateBorderColor(props, theme),
      hoverBorderColor: calculateActiveBorderColor(props, theme),
      activeBorderColor: calculateActiveBorderColor(props, theme),
      borderRadius:
        props.borderRadius ||
        (props.rounded === false ? undefined : convertCssUnit('0.1875rem')),
      width: props.width || (props.block ? 1 : undefined),
      // @ts-expect-error TODO: fix it
      textDecoration: props.variant === 'link' ? 'underline' : 'none',
    };

    return <Component {...propsWithDefaults}>{children}</Component>;
  }

  return Button;
}

const Button = buttonFactory(StyledButton);

export default Button;
export { StyledButton };
